import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import type { User, DeploymentRequest } from './types';
import type { 
  UserRole,
  ActionType
} from './types/deploymentTypes';

import Sidebar, { type AppViewType } from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import DashboardView from './components/Dashboard/DashboardView';
import MyDashboard from './components/Dashboard/MyDashboard';
import GuidedTour from './components/common/GuidedTour';
import SubmitRequestForm from './components/Deployments/SubmitRequestForm';
import DeploymentsList from './components/Deployments/DeploymentsList';
import UserManagement from './components/Users/UserManagement';
import ModuleManagement from './components/Modules/ModuleManagement.new2';
import InstituteManagement from './components/Institutes/InstituteManagement.new2';
import DeploymentReports from './components/Reports/DeploymentReports';
import Settings from './components/Settings/Settings';
import ProfileEdit from './components/Users/ProfileEdit';
import CreateTaskForm from './components/Tasks/CreateTaskForm';
import TaskView from './components/Tasks/TaskView';
import TaskDetail from './components/Tasks/TaskDetail';
import MyTasks from './components/Tasks/MyTasks';
import TaskDashboard from './components/Tasks/TaskDashboard';
import AdvancedReports from './components/Reports/AdvancedReports';
import PerformanceDashboard from './components/Reports/PerformanceDashboard';
import WorkflowManagement from './components/Workflows/WorkflowManagement';
import TimesheetEntryForm from './components/Timesheets/TimesheetEntryForm';
import MyTimesheets from './components/Timesheets/MyTimesheets';
import TeamTimesheets from './components/Timesheets/TeamTimesheets';
import ChatWidget from './components/Chat/ChatWidget';
import { setUnreadFaviconBadge } from './utils/faviconBadge';
import SlotBooking from './components/Deployments/SlotBooking';
import ModuleDeploymentInventory from './components/Deployments/ModuleDeploymentInventory';
import AwsServerInventory from './components/Deployments/AwsServerInventory';
import TLMeetings from './components/Deployments/TLMeetings';
import DeploymentInstructions from './components/Deployments/DeploymentInstructions';
import AIAssistant from './components/AI/AIAssistant';
import AuditLogViewer from './components/Admin/AuditLogViewer';
import ChangelogView from './components/Reports/ChangelogView';
import InstituteStatusView from './components/Institutes/InstituteStatusView';
import NotificationPreferences from './components/Settings/NotificationPreferences';
import type { SearchNavigateTarget } from './components/common/GlobalSearch';
import { getApiUrl } from './config/api';
import './index.css';
import { toast } from './utils/toast';

const DEFAULT_APP_TITLE = 'Deployment Management System';
const ALLOWED_ROLES: UserRole[] = ['admin', 'devops', 'pm', 'tl', 'developer', 'tester'];

// Which deployment-list endpoint each view reads. Views that don't show
// deployments return null, so navigating to them doesn't refetch the list.
function getDeploymentListUrl(view: AppViewType, role: UserRole, userId: string): string | null {
  const baseUrl = '/api/deployments';
  switch (view) {
    case 'dashboard':
    case 'my-dashboard':
      return `${baseUrl}/dashboard/${role}`;
    case 'pm-approvals':
      return `${baseUrl}/approvals/pending/pm`;
    case 'tl-approvals':
      return `${baseUrl}/approvals/pending/tl`;
    case 'my-requests':
      return `${baseUrl}/user/${userId}`;
    case 'all-requests':
      return `${baseUrl}/all?role=${role}`;
    case 'pending-approval':
      return `${baseUrl}/approvals/status/tl_approved`;
    case 'approved':
      return `${baseUrl}/approvals/status/pm_approved`;
    case 'deployments':
      return `${baseUrl}/ready-to-deploy`;
    default:
      return null;
  }
}

function App() {
  // Initialize state and authentication
  const { keycloak, initialized } = useKeycloak();
  const [deployments, setDeployments] = useState<DeploymentRequest[]>([]);
  const [loadingDeployments, setLoadingDeployments] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profilePicture, setProfilePicture] = useState<string | undefined>(undefined);
  
  // Restore activeView from localStorage, or default to dashboard
  const [activeView, setActiveView] = useState<AppViewType>(() => {
    const savedView = localStorage.getItem('activeView');
    return (savedView as AppViewType) || 'dashboard';
  });
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(() => {
    const savedTaskId = localStorage.getItem('selectedTaskId');
    return savedTaskId || null;
  });
  const [, setPreviousView] = useState<AppViewType>(() => {
    const savedPreviousView = localStorage.getItem('previousView');
    return (savedPreviousView as AppViewType) || 'task-dashboard';
  });
  // Mobile (<md) off-canvas sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Set by the header's global search to pre-fill the All Requests search box
  const [listSearchPreset, setListSearchPreset] = useState<{ text: string; nonce: number } | null>(null);
  // Admin-configured branding from GET /api/settings
  const [appSettings, setAppSettings] = useState<{ appName?: string; companyName?: string; supportEmail?: string }>({});
  const appTitle = appSettings.appName || DEFAULT_APP_TITLE;
  const [totalUnreadMessages, setTotalUnreadMessages] = useState<number>(0);

  // Save activeView to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeView', activeView);
  }, [activeView]);

  // Poll for unread messages when NOT in chat view
  useEffect(() => {
    if (activeView === 'chat' || !keycloak.authenticated || !keycloak.subject) {
      return; // Don't poll if in chat view or not authenticated
    }

    const fetchUnreadCount = async () => {
      try {
        // authFetch attaches a freshly refreshed token to /api calls
        const response = await fetch(getApiUrl(`api/chat/channels?userId=${keycloak.subject}`));
        if (response.ok) {
          const channels = await response.json();
          const totalUnread = channels.reduce((sum: number, channel: any) => sum + (channel.unreadCount || 0), 0);
          setTotalUnreadMessages(totalUnread);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    // Initial fetch
    fetchUnreadCount();
    
    // Poll every 30 seconds (reduced from 10s to lower server load)
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(interval);
  }, [activeView, keycloak.authenticated, keycloak.subject]);

  // Reflect unread chat messages in the browser tab: a red badge on the
  // favicon and an "(N)" prefix on the title, like Slack/Gmail. Also fires a
  // desktop notification when the count goes up while this tab is in the
  // background (open but not the active/visible one), so you don't have to
  // be staring at the Chat screen to notice a new message.
  const previousUnreadRef = useRef(0);
  useEffect(() => {
    setUnreadFaviconBadge(totalUnreadMessages > 0);

    if (
      totalUnreadMessages > previousUnreadRef.current &&
      document.hidden &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      try {
        const notification = new Notification('New chat message', {
          body: 'You have unread messages waiting.',
          icon: '/favicon.ico',
          tag: 'chat-unread',
        });
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      } catch (err) {
        console.error('Error showing unread notification:', err);
      }
    }

    previousUnreadRef.current = totalUnreadMessages;
  }, [totalUnreadMessages]);

  // Tab title: admin-configured app name, with an "(N)" unread-chat prefix
  useEffect(() => {
    const n = totalUnreadMessages;
    document.title = n > 0 ? `(${n > 99 ? '99+' : n}) ${appTitle}` : appTitle;
  }, [totalUnreadMessages, appTitle]);

  // Load admin-configured branding once per login
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return;
    let cancelled = false;
    fetch('/api/settings')
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        setAppSettings({
          appName: typeof data.appName === 'string' ? data.appName.trim() : '',
          companyName: typeof data.companyName === 'string' ? data.companyName.trim() : '',
          supportEmail: typeof data.supportEmail === 'string' ? data.supportEmail.trim() : '',
        });
      })
      .catch(err => console.error('Failed to load app settings:', err));
    return () => { cancelled = true; };
  }, [initialized, keycloak.authenticated]);

  // Save selectedTaskId to localStorage whenever it changes
  useEffect(() => {
    if (selectedTaskId) {
      localStorage.setItem('selectedTaskId', selectedTaskId);
    } else {
      localStorage.removeItem('selectedTaskId');
    }
  }, [selectedTaskId]);

  // Debug logging and role validation
  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      import('./utils/keycloakRoles').then(({ debugTokenAndRoles }) => {
        debugTokenAndRoles();
      });
    }
  }, [initialized, keycloak.authenticated]);

  // Fetch profile picture
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!initialized || !keycloak.authenticated || !keycloak.subject) return;
      
      try {
        const response = await fetch(`/api/users/${keycloak.subject}/profile`);
        if (response.ok) {
          const data = await response.json();
          if (data.profilePicture) {
            setProfilePicture(data.profilePicture);
          }
        }
      } catch (err) {
        console.error('Failed to fetch profile picture:', err);
      }
    };

    fetchProfilePicture();
  }, [initialized, keycloak.authenticated, keycloak.subject]);

  // NOTE: the auth early-returns live further down, just before renderContent.
  // They must stay AFTER every hook in this component — returning before a hook
  // runs changes how many hooks are called between the logged-out and
  // logged-in renders, which hung the app on "Loading deployments..." after login.

  // Set up roles and user info.
  // Keyed on primitive values, never the tokenParsed object: Keycloak swaps
  // tokenParsed on every silent token refresh (~5 min), which used to give
  // userInfo a new identity and cascade into refetching the deployment list.
  const realmRolesKey = ((keycloak.tokenParsed?.realm_access?.roles || []) as string[]).join(',');
  const clientRolesKey = ((keycloak.tokenParsed?.resource_access?.['deployment-system']?.roles || []) as string[]).join(',');
  const userRoles = useMemo(() => {
    const all = [...realmRolesKey.split(','), ...clientRolesKey.split(',')]
      .filter(Boolean)
      .map(r => r.toLowerCase());
    return [...new Set(all)];
  }, [realmRolesKey, clientRolesKey]);

  // Highest-priority role wins
  const matchedRole = useMemo(
    () => (ALLOWED_ROLES.find(r => userRoles.includes(r)) || 'developer') as UserRole,
    [userRoles]
  );

  const tokenName = String(keycloak.tokenParsed?.name || keycloak.tokenParsed?.preferred_username || '');
  const tokenEmail = String(keycloak.tokenParsed?.email || '');
  const userInfo = useMemo<User>(() => ({
    id: keycloak.subject || '',
    name: tokenName,
    email: tokenEmail,
    role: matchedRole,
    avatar: profilePicture,
    profilePicture: profilePicture,
  }), [keycloak.subject, tokenName, tokenEmail, matchedRole, profilePicture]);

  // API client. The global authFetch wrapper attaches a freshly refreshed
  // bearer token, so this doesn't capture keycloak.token (which made it — and
  // everything depending on it — change identity on every token refresh).
  // Errors are thrown with the server's { error } message; each caller
  // decides whether that's a toast or the full-page error state.
  const apiClient = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(endpoint, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }, []);

  // The list endpoint for the most recent deployment-showing view. A ref so
  // fetchDeployments stays stable (the auto-refresh interval isn't torn down
  // and rebuilt on every navigation).
  const listUrlRef = useRef<string | null>(null);
  const fetchSeqRef = useRef(0);
  const hasLoadedDeploymentsRef = useRef(false);

  // Fetch deployments for the current list view
  const fetchDeployments = useCallback(async () => {
    const url = listUrlRef.current;
    if (!url) return;
    const seq = ++fetchSeqRef.current;

    // Only the very first load shows the full-screen loader. Background
    // refreshes (auto-refresh, after an action, the Refresh button) keep the
    // current view mounted — swapping it for a loader used to unmount
    // DeploymentsList and throw away search, filters, Show-more, expanded
    // cards and any half-typed reason in an open modal.
    if (!hasLoadedDeploymentsRef.current) setLoadingDeployments(true);

    try {
      const deploymentData = await apiClient(url);
      if (seq !== fetchSeqRef.current) return; // superseded by a newer fetch
      setDeployments(Array.isArray(deploymentData) ? deploymentData : []);
      hasLoadedDeploymentsRef.current = true;
      setError(null);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load deployments';
      console.error('Error loading deployments:', err);
      if (hasLoadedDeploymentsRef.current) {
        toast(`Couldn't refresh deployments: ${message}`, 'error');
      } else {
        setError(message);
      }
    } finally {
      if (seq === fetchSeqRef.current) setLoadingDeployments(false);
    }
  }, [apiClient]);

  // Handle submit request. Throws on failure so SubmitRequestForm keeps
  // everything the user entered.
  const handleSubmitRequest = useCallback(async (request: Partial<DeploymentRequest>) => {
    try {
      // status is computed once, in SubmitRequestForm, from the actual
      // tlApprovals/pmApprovals content — this used to re-derive it here
      // from currentUser.role alone, overwriting that with an unbacked
      // status flag (e.g. every PM submission forced to 'pm_approved' with
      // zero recorded approvals, regardless of what was actually approved).
      await apiClient('/api/deployments', {
        method: 'POST',
        body: JSON.stringify(request)
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit deployment request';
      toast('❌ Failed to submit request: ' + message, 'error');
      throw err;
    }

    // Show success popup
    toast('✅ Deployment request submitted successfully! All assigned TLs will be notified via email.');

    // Switch to dashboard view (its list is fetched on navigation)
    setActiveView('dashboard');
  }, [apiClient]);

  // Handle deployment actions
  const handleAction = useCallback(async (action: ActionType, deploymentId: string, description?: string, releaseNote?: string) => {
    try {
      const deployment = deployments.find(d => d.id === deploymentId || (d as any)._id === deploymentId);
      if (!deployment) {
        throw new Error('Deployment not found');
      }

      const baseUrl = '/api/deployments';
      let endpoint = '';
      const payload: any = {
        actedBy: userInfo,
        actedAt: new Date().toISOString(),
        comments: description || '',
        description: description || '' // Add description field
      };

      switch (action) {
        case 'approve':
          if ((userInfo.role === 'tl' || userInfo.role === 'admin') && deployment.status === 'pending') {
            endpoint = `${baseUrl}/${deploymentId}/approve/tl`;
            // The backend approves as the caller; only an admin may name a TL.
            // Admin approves their own entry if listed, else the next pending TL.
            if (userInfo.role === 'admin') {
              const list: any[] = deployment.tlApprovals || [];
              const entry = list.find(a => !a.approved && a.tl?.id === userInfo.id) || list.find(a => !a.approved);
              if (entry?.tl?.id) payload.tlId = entry.tl.id;
            } else {
              payload.tlId = userInfo.id;
            }
            payload.comments = description || 'Approved by Team Lead';
          } else if ((userInfo.role === 'pm' || userInfo.role === 'admin') && deployment.status === 'tl_approved') {
            endpoint = `${baseUrl}/${deploymentId}/approve/pm`;
            if (userInfo.role === 'admin') {
              const list: any[] = deployment.pmApprovals || [];
              const entry = list.find(a => !a.approved && a.pm?.id === userInfo.id) || list.find(a => !a.approved);
              if (entry?.pm?.id) payload.pmId = entry.pm.id;
            } else {
              payload.pmId = userInfo.id;
            }
            payload.comments = description || 'Approved by Program Manager';
            // Include required releaseNote if provided
            if (releaseNote) payload.releaseNote = releaseNote;
          }
          break;

        case 'deploy':
          if ((userInfo.role === 'devops' || userInfo.role === 'admin') && deployment.status === 'pm_approved') {
            endpoint = `${baseUrl}/${deploymentId}/deploy`;
            payload.comments = description || 'Deployed successfully';
          }
          break;

        case 'emergency-deploy':
          if (userInfo.role === 'devops' || userInfo.role === 'admin') {
            endpoint = `${baseUrl}/${deploymentId}/emergency-deploy`;
            payload.reason = description || '';
            payload.comments = description || '';
          }
          break;

        case 'rollback':
          if (userInfo.role === 'devops' || userInfo.role === 'admin') {
            endpoint = `${baseUrl}/${deploymentId}/rollback`;
            payload.reason = description || '';
          }
          break;

        case 'reject':
          endpoint = `${baseUrl}/${deploymentId}/reject`;
          const rejectionReason = description || prompt('Please provide a reason for rejection:') || `Rejected by ${userInfo.role}`;
          payload.comments = rejectionReason;
          payload.rejectionReason = rejectionReason;
          break;
      }

      if (!endpoint) {
        throw new Error('Invalid action for current role and deployment status');
      }

      await apiClient(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Show success message
      if (action === 'approve') {
        toast('✅ Deployment approved successfully! Notifications have been sent.');
      } else if (action === 'deploy') {
        toast('✅ Deployment completed successfully! Notifications have been sent.');
      } else if (action === 'reject') {
        toast('❌ Deployment rejected. The requestor has been notified.');
      } else if (action === 'emergency-deploy') {
        toast('🚨 Emergency deploy recorded. Approval will continue in the background to formally close this request.');
      } else if (action === 'rollback') {
        toast('⏪ Rollback recorded. The requestor has been notified and any booked slot was released.');
      }

      // Background refresh — the view stays mounted
      void fetchDeployments();
    } catch (err) {
      // An action failing is not a page failure: toast the server's reason
      // and rethrow so DeploymentCard keeps its modal (and typed text) open.
      const message = err instanceof Error ? err.message : `Failed to ${action} deployment`;
      toast(`❌ ${message}`, 'error');
      throw err;
    }
  }, [apiClient, deployments, fetchDeployments, userInfo]);

  // Task handlers
  const handleTaskCreated = useCallback(() => {
    setActiveView('task-mytasks');
  }, []);

  const handleSelectTask = useCallback((taskId: string) => {
    // Save current view as previous view before navigating to task detail
    setPreviousView(activeView);
    localStorage.setItem('previousView', activeView);
    setSelectedTaskId(taskId);
    setActiveView('task-detail');
  }, [activeView]);

  const handleBackToTasks = useCallback(() => {
    setSelectedTaskId(null);
    // Go back to the previous view, or default to task-dashboard
    const savedPreviousView = localStorage.getItem('previousView') as AppViewType;
    setActiveView(savedPreviousView || 'task-dashboard');
  }, []);
  
  // Fetch the deployment list when navigating to a view that shows it. Views
  // that don't use it (tasks, timesheets, settings…) don't trigger a refetch.
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return;
    const url = getDeploymentListUrl(activeView, userInfo.role, userInfo.id);
    if (url) {
      listUrlRef.current = url;
      fetchDeployments();
    } else if (!listUrlRef.current) {
      // Opened straight onto a non-list view: still load the dashboard set
      // once, since the AI assistant reads it.
      listUrlRef.current = getDeploymentListUrl('dashboard', userInfo.role, userInfo.id);
      fetchDeployments();
    }
  }, [initialized, keycloak.authenticated, activeView, userInfo.role, userInfo.id, fetchDeployments]);

  // Auto-refresh deployments every 60 seconds (only on specific screens)
  // Increased from 30s to 60s to reduce interruptions when users are taking actions
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return;
    
    // Only auto-refresh on these specific screens
    const refreshableViews: AppViewType[] = ['dashboard', 'my-dashboard', 'all-requests', 'my-requests', 'tl-approvals', 'pm-approvals'];
    if (!refreshableViews.includes(activeView)) return;

    const refreshInterval = setInterval(() => {
      fetchDeployments();
    }, 120000); // Refresh every 2 minutes

    return () => clearInterval(refreshInterval);
  }, [initialized, keycloak.authenticated, activeView, fetchDeployments]);

  // Effect to update activeView based on role (only on first load if no saved view)
  useEffect(() => {
    const savedView = localStorage.getItem('activeView');
    // Only set default view if there's no saved view (first time user)
    if (!savedView && (matchedRole === 'tl' || matchedRole === 'pm')) {
      setActiveView('submit');
    }
  }, [matchedRole]);

  // Handle authentication state (after all hooks — see note above)
  if (!initialized) {
    return <div className="flex items-center justify-center min-h-screen text-lg">Initializing authentication...</div>;
  }

  if (!keycloak.authenticated) {
    keycloak.login();
    return <div className="flex items-center justify-center min-h-screen text-lg">Redirecting to login...</div>;
  }

  // Handle rendering different views based on user role
  // Plain function, not useCallback: it's invoked immediately below in this
  // same render's JSX (never passed down or used in a dependency array), so
  // memoizing it bought nothing but risk — a function declared fresh every
  // render can never read stale state.
  const renderContent = () => {
    // Full-screen loader / error only for the initial load of a view that
    // actually shows deployments (fetchDeployments never re-enters loading
    // once data has arrived, so refreshes don't unmount the view).
    const showsDeployments = getDeploymentListUrl(activeView, userInfo.role, userInfo.id) !== null;
    if (showsDeployments && loadingDeployments) {
      return <div className="flex items-center justify-center min-h-[200px] text-lg">Loading deployments...</div>;
    }

    if (showsDeployments && error) {
      return (
        <div className="flex items-center justify-center min-h-screen text-lg text-red-600">
          <div className="bg-red-100 p-4 rounded-lg">
            <h3 className="font-bold mb-2">Error</h3>
            <p>{error}</p>
            <button
              onClick={() => {
                setError(null);
                fetchDeployments();
              }}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    const commonDashboard = <DashboardView currentUser={userInfo} deployments={deployments} />;
    const commonMyDashboard = <MyDashboard currentUser={userInfo} deployments={deployments} />;
    const commonSubmitForm = (
      <SubmitRequestForm
        currentUser={userInfo}
        onSubmit={handleSubmitRequest}
      />
    );
    const commonProfileEdit = <ProfileEdit userId={userInfo.id} currentUser={userInfo} onCancel={() => setActiveView('dashboard')} />;
    const allRequestsList = (
      <DeploymentsList
        title="All Deployment Requests"
        searchPreset={listSearchPreset}
        deployments={deployments}
        currentUser={userInfo}
        onAction={handleAction}
        onRefresh={fetchDeployments}
      />
    );
    
    // Handle profile view for all roles
    if (activeView === 'profile') {
      return commonProfileEdit;
    }

    const ALL_ROLES: UserRole[] = ['admin', 'tl', 'pm', 'devops', 'developer', 'tester'];
    const goToTaskDashboard = () => setActiveView('task-dashboard');
    const goToMyTimesheets = () => setActiveView('my-timesheets');

    // Single declarative table replacing what used to be six separate, hand-copied
    // per-role switch statements. Each view-id is defined exactly ONCE, with the
    // roles allowed to see it listed alongside it — this is what a missing case in
    // one role's copy used to cause (e.g. admin's switch once had no 'submit' case
    // at all, so "Use Template" silently landed on the dashboard for admins only).
    // Role-specific differences that used to require duplicating a whole case
    // (like devops getting an extra onViewAllTasks prop on task-dashboard) are now
    // just a role check inside that view's own render() below.
    const viewRegistry: Partial<Record<AppViewType, { roles: UserRole[]; render: () => JSX.Element }>> = {
      'dashboard': { roles: ALL_ROLES, render: () => commonDashboard },
      'my-dashboard': { roles: ALL_ROLES, render: () => commonMyDashboard },

      'submit': { roles: ['admin', 'tl', 'pm', 'developer'], render: () => commonSubmitForm },

      'all-requests': { roles: ALL_ROLES, render: () => allRequestsList },

      'my-requests': {
        roles: ['tl', 'pm', 'developer'],
        render: () => (
          <DeploymentsList
            title="My Requests"
            deployments={deployments}
            currentUser={userInfo}
            onAction={handleAction}
            onRefresh={fetchDeployments}
            filterBy="my-requests"
          />
        )
      },

      'tl-approvals': {
        roles: ['tl'],
        render: () => (
          <DeploymentsList
            title="TL Approvals"
            deployments={deployments.filter(d => {
              // Filter: status is pending, not requested by current user
              if (d.status !== 'pending' || d.requestedBy?.id === userInfo.id) {
                return false;
              }
              // Check if current TL is assigned to this deployment
              if (Array.isArray(d.tlApprovals)) {
                const currentUserApproval = d.tlApprovals.find(
                  (tl: any) => tl.tl?.id === userInfo.id || tl.tl?.email === userInfo.email
                );
                if (!currentUserApproval) return false; // Not assigned to this TL
                if (currentUserApproval.approved) return false; // Already approved
                return true; // Assigned and pending approval
              }
              return false; // No TL approvals array
            })}
            currentUser={userInfo}
            onAction={handleAction}
            onRefresh={fetchDeployments}
            filterBy="pending"
          />
        )
      },

      'pm-approvals': {
        roles: ['pm'],
        render: () => (
          <DeploymentsList
            title="PM Approvals"
            deployments={deployments.filter(d => {
              if (d.status !== 'tl_approved') return false;
              if (Array.isArray(d.pmApprovals)) {
                const currentUserApproval = d.pmApprovals.find(
                  (pm: any) => pm.pm?.id === userInfo.id || pm.pm?.email === userInfo.email
                );
                if (!currentUserApproval) return false; // Not assigned to this PM
                if (currentUserApproval.approved) return false; // Already approved
                return true; // Assigned and pending approval
              }
              return false; // No PM approvals array
            })}
            currentUser={userInfo}
            onAction={handleAction}
            onRefresh={fetchDeployments}
            filterBy="pending"
          />
        )
      },

      'pending-approval': {
        roles: ['pm'],
        render: () => (
          <DeploymentsList
            title="Pending Approvals"
            deployments={deployments.filter(d => {
              if (d.status !== 'tl_approved') return false;
              if (Array.isArray(d.pmApprovals)) {
                const currentUserApproval = d.pmApprovals.find(
                  (pm: any) => pm.pm?.id === userInfo.id || pm.pm?.email === userInfo.email
                );
                if (!currentUserApproval) return false;
                if (currentUserApproval.approved) return false;
                return true;
              }
              return false;
            })}
            currentUser={userInfo}
            onAction={handleAction}
            onRefresh={fetchDeployments}
            filterBy="pending"
          />
        )
      },

      'approved': {
        roles: ['pm'],
        render: () => (
          <DeploymentsList
            title="Approved Requests"
            deployments={deployments.filter(d => d.status === 'pm_approved')}
            currentUser={userInfo}
            onAction={handleAction}
            onRefresh={fetchDeployments}
            filterBy="approved"
          />
        )
      },

      'deployments': {
        roles: ['devops'],
        render: () => (
          <DeploymentsList
            title="Ready to Deploy"
            deployments={deployments}
            currentUser={userInfo}
            onAction={handleAction}
            onRefresh={fetchDeployments}
            filterBy="approved"
          />
        )
      },

      'module-deployment-inventory': { roles: ALL_ROLES, render: () => <ModuleDeploymentInventory currentUser={userInfo} /> },
      'aws-server-inventory': { roles: ['devops', 'pm', 'admin'], render: () => <AwsServerInventory currentUser={userInfo} /> },
      'tl-meetings': { roles: ['tl', 'admin'], render: () => <TLMeetings currentUser={userInfo} /> },
      'deployment-instructions': { roles: ALL_ROLES, render: () => <DeploymentInstructions currentUser={userInfo} /> },
      'reports': { roles: ['admin', 'tl', 'pm'], render: () => <DeploymentReports /> },
      'advanced-reports': { roles: ALL_ROLES, render: () => <AdvancedReports currentUser={userInfo} /> },
      'performance-dashboard': { roles: ['admin', 'devops'], render: () => <PerformanceDashboard currentUser={userInfo} /> },
      'slot-booking': { roles: ['tl', 'pm', 'devops'], render: () => <SlotBooking currentUser={userInfo} /> },
      'users': { roles: ['admin'], render: () => <UserManagement /> },
      'modules': { roles: ['admin'], render: () => <ModuleManagement /> },
      'institutes': { roles: ['admin'], render: () => <InstituteManagement /> },
      'settings': { roles: ['admin'], render: () => <Settings currentUser={userInfo} /> },
      'workflows': { roles: ['admin', 'devops'], render: () => <WorkflowManagement currentUser={userInfo} /> },
      'team-timesheets': { roles: ['admin', 'tl', 'pm', 'devops'], render: () => <TeamTimesheets currentUser={userInfo} /> },

      'audit-log': { roles: ['admin'], render: () => <AuditLogViewer /> },
      'changelog': { roles: ALL_ROLES, render: () => <ChangelogView /> },
      'institute-status': { roles: ['pm', 'devops', 'admin'], render: () => <InstituteStatusView /> },

      'task-dashboard': {
        roles: ALL_ROLES,
        render: () => (
          <TaskDashboard
            currentUser={userInfo}
            onSelectTask={handleSelectTask}
            onCreateTask={() => setActiveView('task-create')}
            onViewMyTasks={() => setActiveView('task-mytasks')}
            {...(userInfo.role === 'devops' ? { onViewAllTasks: () => setActiveView('task-list') } : {})}
          />
        )
      },
      'task-create': { roles: ALL_ROLES, render: () => <CreateTaskForm currentUser={userInfo} onTaskCreated={handleTaskCreated} onBack={goToTaskDashboard} /> },
      'task-mytasks': { roles: ALL_ROLES, render: () => <MyTasks currentUser={userInfo} onSelectTask={handleSelectTask} onBack={goToTaskDashboard} /> },
      'task-list': { roles: ALL_ROLES, render: () => <TaskView currentUser={userInfo} onSelectTask={handleSelectTask} onBack={goToTaskDashboard} /> },
      'task-detail': {
        roles: ALL_ROLES,
        render: () => selectedTaskId
          ? <TaskDetail taskId={selectedTaskId} currentUser={userInfo} onBack={handleBackToTasks} />
          : commonDashboard
      },

      'timesheet-entry': { roles: ALL_ROLES, render: () => <TimesheetEntryForm currentUser={userInfo} onSuccess={goToMyTimesheets} /> },
      'my-timesheets': { roles: ALL_ROLES, render: () => <MyTimesheets currentUser={userInfo} /> },

      'my-preferences': {
        roles: ALL_ROLES,
        render: () => (
          <div className="max-w-4xl mx-auto">
            <NotificationPreferences userId={userInfo.id} userName={userInfo.name} />
          </div>
        )
      },
    };

    const entry = viewRegistry[activeView];
    if (entry && entry.roles.includes(userInfo.role)) {
      return entry.render();
    }
    return commonDashboard;
  };
  const handleViewChange = (view: AppViewType) => {
    setListSearchPreset(null); // a search pre-fill applies to one navigation only
    setActiveView(view);
    setSidebarOpen(false);
  };

  // Header global-search result → in-app navigation
  const handleSearchNavigate = (target: SearchNavigateTarget) => {
    setSidebarOpen(false);
    switch (target.type) {
      case 'deployment':
        // All Requests is visible to every role; pre-fill its search box
        setListSearchPreset({ text: target.title || '', nonce: Date.now() });
        setActiveView('all-requests');
        break;
      case 'task':
        handleSelectTask(target.id);
        break;
      case 'module':
        setActiveView(userInfo.role === 'admin' ? 'modules' : 'module-deployment-inventory');
        break;
      case 'institute':
        if (userInfo.role === 'admin') setActiveView('institutes');
        else if (userInfo.role === 'pm' || userInfo.role === 'devops') setActiveView('institute-status');
        else toast(`Institute: ${target.title || target.id}`, 'info');
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        currentUser={userInfo}
        activeView={activeView}
        onViewChange={handleViewChange}
        unreadMessagesCount={totalUnreadMessages}
        mobileOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        appName={appSettings.appName}
        companyName={appSettings.companyName}
        supportEmail={appSettings.supportEmail}
      />
      <div className="md:ml-64 flex flex-col min-h-screen min-w-0">
        <Header
          currentUser={userInfo}
          onProfileClick={() => setActiveView('profile')}
          onChatClick={() => setActiveView('chat')}
          unreadMessagesCount={totalUnreadMessages}
          onMenuClick={() => setSidebarOpen(true)}
          appTitle={appTitle}
          onSearchNavigate={handleSearchNavigate}
        />
        <main className="flex-1 p-3 sm:p-6 bg-white rounded-lg shadow-md mx-2 sm:mx-4 mt-4 mb-4 min-w-0">
          {renderContent()}
        </main>
      </div>

      {/* Chat Widget - Only show when chat view is active */}
      {activeView === 'chat' && <ChatWidget currentUser={userInfo} onUnreadCountChange={setTotalUnreadMessages} />}
      
      {/* AI Assistant - Always available (except in chat view to avoid clutter) */}
      {activeView !== 'chat' && <AIAssistant currentUser={userInfo} deployments={deployments} />}

      <GuidedTour />
    </div>
  );
}

export default App;
